
import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { searchUnits } from '../../../core/api/client';
import i18n from '../../../i18n';

export interface SearchFilters {
    q: string;
    location: string[];
    category: string[];
    minPrice: string;
    maxPrice: string;
    minArea: string;
    maxArea: string;
    beds: string[];
    baths: string[];
    constructionStatus: string[];
    finishType: string[];
    sort: string;
    page: number;
}

const initialFilters: SearchFilters = {
    q: '',
    location: [],
    category: [],
    minPrice: '',
    maxPrice: '',
    minArea: '',
    maxArea: '',
    beds: [],
    baths: [],
    constructionStatus: [],
    finishType: [],
    sort: 'newest',
    page: 1
};

export const useSearch = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState<SearchFilters>(initialFilters);

    // Sync URL params to State on load & change
    useEffect(() => {
        const newFilters = { ...initialFilters };
        if (searchParams.has('q')) newFilters.q = searchParams.get('q') || '';

        // Helper to split comma-separated values
        const getList = (key: string) => {
            const val = searchParams.get(key);
            console.log(`DEBUG: Parsing ${key} from URL:`, val);
            return val ? val.split(',') : []; // Ensure splitting is correct
        };

        if (searchParams.has('location')) newFilters.location = getList('location');
        if (searchParams.has('category')) {
            newFilters.category = getList('category');
            console.log('DEBUG: Parsed Categories:', newFilters.category);
        }
        if (searchParams.has('minPrice')) newFilters.minPrice = searchParams.get('minPrice') || '';
        if (searchParams.has('maxPrice')) newFilters.maxPrice = searchParams.get('maxPrice') || '';
        if (searchParams.has('minArea')) newFilters.minArea = searchParams.get('minArea') || '';
        if (searchParams.has('maxArea')) newFilters.maxArea = searchParams.get('maxArea') || '';
        if (searchParams.has('beds')) newFilters.beds = getList('beds');
        if (searchParams.has('baths')) newFilters.baths = getList('baths');
        if (searchParams.has('constructionStatus')) newFilters.constructionStatus = getList('constructionStatus');
        if (searchParams.has('finishType')) newFilters.finishType = getList('finishType');
        if (searchParams.has('sort')) newFilters.sort = searchParams.get('sort') || 'newest';
        if (searchParams.has('page')) newFilters.page = parseInt(searchParams.get('page') || '1');

        console.log('DEBUG: Setting Filters from URL:', newFilters);
        setFilters(newFilters);
    }, [searchParams]);
    // Query Configuration
    const queryParams = useMemo(() => ({
        q: filters.q,
        location: filters.location,
        category: filters.category,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        minArea: filters.minArea,
        maxArea: filters.maxArea,
        beds: filters.beds,
        baths: filters.baths,
        constructionStatus: filters.constructionStatus,
        finishType: filters.finishType,
        sort: filters.sort,
        page: filters.page,
        limit: 20,
        locale: i18n.language
    }), [filters, i18n.language]);

    // Server-side Search
    const { data, isLoading, isError, isFetching } = useQuery({
        queryKey: ['search', queryParams],
        queryFn: () => searchUnits(queryParams),
        staleTime: 1000 * 60, // 1 minute
        placeholderData: (previousData) => previousData, // smooth transitions
    });

    const results = data?.data || [];
    const totalResults = data?.pagination?.total || 0;
    const totalPages = data?.pagination?.pages || 1;

    // Update Filters (and URL)
    const updateFilters = (key: keyof SearchFilters, value: any) => {
        const newFilters = { ...filters, [key]: value };

        // Reset page to 1 if filter changes (except page itself)
        if (key !== 'page') newFilters.page = 1;

        setFilters(newFilters);

        // Update URL
        const params = new URLSearchParams();
        if (newFilters.q) params.set('q', newFilters.q);
        if (newFilters.minPrice) params.set('minPrice', newFilters.minPrice);
        if (newFilters.maxPrice) params.set('maxPrice', newFilters.maxPrice);
        if (newFilters.minArea) params.set('minArea', newFilters.minArea);
        if (newFilters.maxArea) params.set('maxArea', newFilters.maxArea);
        if (newFilters.sort) params.set('sort', newFilters.sort);
        if (newFilters.page > 1) params.set('page', newFilters.page.toString());

        // Use comma-separated strings for arrays to keep URL clean
        if (newFilters.location.length > 0) params.set('location', newFilters.location.join(','));
        if (newFilters.category.length > 0) params.set('category', newFilters.category.join(','));
        if (newFilters.beds.length > 0) params.set('beds', newFilters.beds.join(','));
        if (newFilters.baths.length > 0) params.set('baths', newFilters.baths.join(','));
        if (newFilters.constructionStatus.length > 0) params.set('constructionStatus', newFilters.constructionStatus.join(','));
        if (newFilters.finishType.length > 0) params.set('finishType', newFilters.finishType.join(','));

        setSearchParams(params);
    };

    return {
        results,
        totalResults,
        totalPages,
        filters,
        updateFilters,
        isLoading: isLoading || isFetching,
        isError
    };
};
